import React, {
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	get,
	map,
} from 'lodash';
import cx from 'classnames';

import CmsContentList from 'components/data/CmsContentList.js';
import CmsContentRenderer from 'components/data/CmsContentRenderer.js';
import CmsContentRendered from 'components/data/CmsContentRendered.js';
import { findPrimaryToken } from 'components/manage-cards/TokenHelpers.js';
import ReasonOptions, {
	REASON_TYPE_PARAM,
	ACTION_TYPE_PARAM,
	RESOLUTION_TYPE_PARAM,
	BLOCKED_REASON_MAPPING,
} from 'pages/account/card/replacement/ReasonOptions.js';
import { REASON_CODES } from 'components/data/customer-service/ReasonCodes.query.js';
import ResolutionOptions from 'pages/account/card/replacement/ResolutionOptions.js';
import Button from 'components/Button.js';
import Input from 'components/forms/Input.js';

import { RadioGroup } from '@headlessui/react';
import { useOptionsSelectedContext } from 'pages/account/card/replacement/OptionsSelectedProvider.js';
import { FormHelperProvider, useFormHelperContext } from 'utils/form-helper/FormHelperProvider.js';

import {
	useLocation,
	Redirect,
} from "react-router-dom";
import {
	object as yup_object,
	string as yup_string,
} from "yup";

import {
	levels,
	noticeError,
} from 'utils/Logger.js';

import { parse } from "qs";

import { getPathByRoute } from 'App.js';
import routeKeys from 'CustomerRouteKeys.js';
import PreventDefault from 'utils/PreventDefault.js';
import GoogleAnalytics from 'utils/analytics/GoogleAnalytics.js';
import FormHelper from 'utils/FormHelper.js';
import { SUBSYSTEM_ID, TOKEN } from 'utils/Constants';
import { graphqlErrorMiddleware } from "utils/error-handling/graphql/GraphqlClientMiddleware.js";
import { cancelAutoloadMutation } from 'pages/account/CancelReload.js';

import { getTransitAccountRefetchQueries, useTransitAccount } from "components/data/transit-account/TransitAccount.js";
import { useStepContext, STEP_OFFSET_PARAM } from 'context/StepContext.js';
import { useGlobalToastsContext } from 'context/ToastProvider.js';
import { Lifecycles } from "libreact/lib/Lifecycles/index.js";

import {
	CARD_ACTIONS,
	CARD_RESOLUTIONS,
} from 'pages/account/card/replacement/constants.js';
import useFormHelper from 'utils/form-helper/useFormHelper.js';
import Toast, { TYPE_ERROR } from "components/Toast.js";

import WSMediaProduct from 'server/api-types/WSMediaProduct.js';
import { OPEN_TRANSIT_REGULAR_CARD, OPEN_TRANSIT_RESTRICTED_CARD, OPEN_TRANSIT_TEMPORARY_CARD } from 'server/api-types/WSSubsystemAccountToken.js';
import { useCartContext } from 'context/CartProvider.js';
import { useFundingSourcesContext } from 'context/FundingSourcesContext.js';
import useStandardMutation from 'components/data/hooks/useStandardMutation.js';
import WSIssueMediaLineItem from 'server/api-types/WSIssueMediaLineItem.js';
import WSIssueSubsystemMedia from 'server/api-types/WSIssueSubsystemMedia.js';

import { GET_REPLACE_PAYMENT_METHODS } from 'components/data/order/replace/Paymentmethods.query.js';
import {
	BLOCK_OR_REPLACE_CARD,
	BYPASS_TO_REPLACEMENT,
	PURCHASE_REPLACEMENT,
	TRANSFER_REPLACEMENT,
} from 'pages/account/card/BlockOrReplaceFlow.js';
import { TOKEN_ACTION } from 'components/data/transit-account/subsystem/travel-token/TokenAction.mutation';
import { ComponentLoading } from 'components/icons/LoadingIcon.js';


import * as takeover from 'layouts/TakeOverLayout.module.css';
import * as forms from 'components/forms/Forms.module.css';
import * as style from 'pages/account/card/BlockOrReplaceFlow.module.css';
import WSMediaRestriction from "server/api-types/WSMediaRestriction";
import WSProductDisplay from "server/api-types/WSProductDisplay";
import { useReplacementOptions } from "components/data/transit-account/ReplacementOptions.query";
import { useReasonCodeFees } from "components/data/transit-account/ReasonCodeFees";
import { useApolloClient } from "@apollo/client";
import { useTransitAccountIdContext } from "context/TransitAccountIdContext";



const cms = {
	description: 'miscText.card-freeze-header',
	subHeader: 'miscText.card-freeze-action-subheader',

	blockAction: 'miscText.card-freeze-action-freeze',
	blockActionDetails: 'miscHtml.card-freeze-description',

	replaceAction: 'miscText.card-freeze-action-replace',
	replaceActionDetails: 'miscHtml.card-freeze-action-replace-details',
	continueButton: 'miscText.card-freeze-submit',

	toastBlock: 'miscText.card-freeze-confirmation',
	required: 'miscText["errors.general.blankfield"]',

	invalidMediaProductToast: 'miscText.invalid-media-product-toast-header',
};

const getYupSchema = (step,actionType) => {
	const validations = {
		actionType: yup_string().required(cms.required),
	};

	if (step === BLOCK_OR_REPLACE_CARD) {
		validations[ 'reasonType' ] = yup_string().required(cms.required);

	}
	if (actionType === CARD_ACTIONS.replaceCard) {
		validations[ 'resolutionType' ] = yup_string().required(cms.required);
	}

	return yup_object().shape(validations);
};

const getActionMapping = (canBlockOnly) => {
	return {
		[ CARD_ACTIONS.blockCard ]: {
			action: 'blockAction',
			actionFallback: 'Block card',
			actionDetails: 'blockActionDetails',
			actionDetailsFallback: '<p>You will not be able to use this card for travel while it is blocked. You can unblock this card it at any time.</p>',
		},
		...(!canBlockOnly && {
			[ CARD_ACTIONS.replaceCard ]: {
				action: 'replaceAction',
				actionFallback: 'Replace card',
				actionDetails: 'replaceActionDetails',
				actionDetailsFallback: '<p>You can order a new card as a replacement, or use a card already on your account.</p>',
			},
		}),
	};
};
const CardActionSelector = ({ canBlockOnly, canOrderCard }) => {
	const { formHelper } = useFormHelperContext();
	const { actionType, setActionType } = useOptionsSelectedContext();

	const actionMapping = getActionMapping(canBlockOnly);

	return (
		<CmsContentList list={Object.values(cms)}>{() => (
			<>
				{!canBlockOnly && <>

					<CmsContentRenderer.Div
						contentKey={cms.description}
						fallbackValue="<p>Block your card to prevent usage in the Charlie system. You can unblock this card, order a replacement card, or transfer balance and passes from this card to a new card. If your card was defective, you may <a href='/deep-link/contact-us'>contact us</a> to see if you're eligible for a free replacement.</p>"
					/>

					<section className={style.actionSelectionSection}>
						<RadioGroup value={actionType}
							onChange={(value) => setActionType(value)}
							className={forms.radioGroup}
						>
							<RadioGroup.Label>
								<CmsContentRenderer.P
									className={style.sectionHeader}
									contentKey={cms.subHeader}
									fallbackValue="Do you want to block your card, or replace it?"
								/>
							</RadioGroup.Label>

							<div className={forms.radioGroupOptions}>
								{map(actionMapping, (value, key) =>
									<RadioGroup.Option
										key={key}
										value={key}
										checked={actionType === key}
										className={cx(forms.radioGroupOption)}
									>
										{({ checked, active }) => (<>
											<CmsContentRenderer.P
												className={cx(forms.radioLabel, checked && forms.radioChecked)}
												contentKey={cms[ value.action ]}
												fallbackValue={value.actionFallback}
											/>

											<CmsContentRenderer.Div
												className={style.actionDetails}
												contentKey={cms[ value.actionDetails ]}
												fallbackValue={value.actionDetailsFallback}
											/>
										</>)}
									</RadioGroup.Option>
								)}
							</div>
						</RadioGroup>
					</section>
				</>}

				<Input
					key={actionType}
					type="hidden"
					name='actionType'
					value={actionType}
					controlled={true}
					hideLabel={true}
					error={formHelper.getFieldError('actionType')}
				/>

				{(actionType === CARD_ACTIONS.replaceCard || actionType === CARD_ACTIONS.blockCard)
					? <ReasonOptions {...{ reasonTypeId: actionType === CARD_ACTIONS.blockCard ? REASON_CODES.BlockTravelToken : REASON_CODES.ReplaceTravelToken }} />
					: null}
				{actionType === CARD_ACTIONS.replaceCard
					? <ResolutionOptions canOrderCard={canOrderCard} />
					: null}
			</>
		)}</CmsContentList>
	);
};

export const replaceTransitAccountMedia ='ReplaceTransitAccountMedia';
export const upgradeTransitAccountMedia = 'UpgradeTransitAccountMedia';

const BlockOrReplaceSelect = () => {
	const subsystemAccountReference  = useTransitAccountIdContext();
	const apolloClient = useApolloClient();
	const location = useLocation();
	const [ isInvalidMediaProduct, setIsInvalidMediaProduct ] = useState(false);

	// Once we migrate to React-router 6 we can use the `useSearchParams` hook.
	const {
		[ REASON_TYPE_PARAM ]: seachParamReasonType = null,
		[ ACTION_TYPE_PARAM ]: seachParamActionType = null,
		[ RESOLUTION_TYPE_PARAM ]: seachParamResolutionType = null,
		[ STEP_OFFSET_PARAM ]: seachParamStepOffSet = '0', // string not int
	} = parse(location.search, { ignoreQueryPrefix: true });

	const stepOffSet = parseInt(seachParamStepOffSet, 10);

	const {
		actionType,
		reasonType,
		resolutionType,
		setNotes,
		setActionType,
	} = useOptionsSelectedContext();

	const {
		data: transitAccountQdata,
		loading,
	} = useTransitAccount({ subsystemAccountReference });

	const { tokens, riderClass } = transitAccountQdata?.transitAccountQ ?? {};
	const primaryToken = findPrimaryToken(tokens);

	// If the card is temporary we need to get upgrade replacementOptions
	const replacementType = primaryToken?.mediaType === OPEN_TRANSIT_TEMPORARY_CARD
		? upgradeTransitAccountMedia
		: replaceTransitAccountMedia;

	const {
		mediaProducts,
		queryResult,
	} = useReplacementOptions({ tokenId: primaryToken?.tokenId, replacementType,  subsystemAccountReference });

	const {
		addProduct,
		saveShoppingCartId,
	} = useCartContext();

	const { initializeFundingSources } = useFundingSourcesContext();
	const [ getReplacePaymentMethods ] = useStandardMutation(GET_REPLACE_PAYMENT_METHODS);

	const { step, setStep } = useStepContext();
	const {
		formRef,
		formHelper,
		validationState, setValidationState,
		submitting, setSubmitting,
	} = useFormHelper({ getYupSchema: () => getYupSchema(step,actionType) });

	const formHelperContext = {
		formRef,
		formHelper,
		validationState, setValidationState,
		submitting, setSubmitting,
	};

	const [ redirect, setRedirect ] = useState(null);
	const { setToast, removeToast } = useGlobalToastsContext();

	const charlieCard = useMemo(() =>
		mediaProducts?.find(prod =>
			prod.mediaTypeName === OPEN_TRANSIT_REGULAR_CARD &&
			prod.mediaRestrictions.some(rest => rest.mediaRestrictionName === "Replace")
		) ?? null
	, [ mediaProducts ]);

	const {	replacementFeeObj } = useReasonCodeFees({
		subsystemAccountReference,
		tokenId: primaryToken?.tokenId,
		reasonCodeId: reasonType?.reasonCodeId,
		mediaSKU: charlieCard?.mediaSKU,
	});

	const isTempCard = primaryToken?.mediaType === OPEN_TRANSIT_TEMPORARY_CARD;
	const isHandsFreeCard = primaryToken?.mediaType === OPEN_TRANSIT_RESTRICTED_CARD;

	const canBlockOnly = isHandsFreeCard || isTempCard;

	useEffect(() => {
		if (!actionType && canBlockOnly) {
			setActionType(CARD_ACTIONS.blockCard);
		}
	}, [ canBlockOnly, actionType, setActionType ]);

	// We are waiting for hook data to return
	// OR
	// the User has bypassed step 1 and we have submitted the form once it mounted.
	if (loading || queryResult.loading ||
		(stepOffSet === BYPASS_TO_REPLACEMENT && submitting)) {
		return <ComponentLoading />;
	}

	const onBlockSuccess = () => {
		setToast(
			<Toast
				type="success"
				title={<CmsContentRendered.Span
					contentKey={cms.toastBlock}
					fallbackValue={'Your card has been blocked. You can choose to resume use, replace, or terminate the card at any time.'}
				/>}
				onClosed={removeToast}
			/>,
		);

		setRedirect(
			<Redirect push to={{
				pathname: getPathByRoute(routeKeys.AccountCardSettings, {
					transit_account_id: subsystemAccountReference,
				}),
				state: { success: true },
			}} />,
		);
	};

	const kickoffSubmit = async () => {
		setSubmitting(true);

		// dont validate the form we are about to redirect using param values
		if (stepOffSet !== BYPASS_TO_REPLACEMENT) {

			let validated;
			try {
				validated = await formHelper.startValidation(true);
			}
			catch (errorReport) {
				setSubmitting(false);
				// yup validation failed, but those errors are already in state
				formHelper.validationErrorHandler(errorReport);
				return;
			}
			setNotes(validated[ `${reasonType.reasonCodeId}_message` ]);
		}

		if (actionType === CARD_ACTIONS.replaceCard && resolutionType === CARD_RESOLUTIONS.currentCard) {
			setStep(TRANSFER_REPLACEMENT);
			return;
		}

		if (
			(actionType === CARD_ACTIONS.replaceCard && resolutionType === CARD_RESOLUTIONS.newCard) ||
			(seachParamActionType === CARD_ACTIONS.replaceCard && seachParamResolutionType === CARD_RESOLUTIONS.newCard)
		) {

			// This would happen if the user came from the Replace flow and we auto submit from page load.
			// If there is no valid charlieCard we can't let the user proceed with the flow
			if (!charlieCard) {
				setIsInvalidMediaProduct(true);
				setToast(<Toast
					type={TYPE_ERROR}
					title={<CmsContentRendered.Span
						contentKey={cms.invalidMediaProductToast}
						fallbackValue="Sorry, this feature is currently unavailable for this card."
					/>}
				/>);
				return;
			}

			// If we came from the replace flow directly we won't have a replacementFeeObj so we will get it during the
			// See USE_EFFECT_BY_PASS in PurchaseReplacementCharlieCardCcForm
			const replacementFeeAmount = stepOffSet !== BYPASS_TO_REPLACEMENT && replacementFeeObj.chargeReplacementFee
				? replacementFeeObj.replacementFeeAmount
				: 0;

			try {
				const paymentMethodsRes = await getReplacePaymentMethods({
					variables: {
						issueMediaLineItems: [
							new WSIssueMediaLineItem({
								issueMedia: new WSIssueSubsystemMedia({
									...charlieCard,
									subsystem: SUBSYSTEM_ID,
									type: 'TransitAccountMedia',
									subsystemAccountReference,
									replacementFeeAmount,
									replacedTravelTokenId: primaryToken.tokenId,
									enablementFeeAmount: 0,
									riderClassId: riderClass,
									itemTotalAmount: replacementFeeAmount,
									mediaType: OPEN_TRANSIT_REGULAR_CARD,
									waiveEnableFeeFlag: false,
									replacedTokenReasonCode: stepOffSet === BYPASS_TO_REPLACEMENT
										? seachParamReasonType
										: reasonType.reasonCodeId,
								}),
							}).toResolver(),
						],
					},
				});

				const {
					shoppingCartId,
					loadProductLineItemFunds,
					fundingSources,
					restrictFunding,
				} = paymentMethodsRes.data.OrderRoute.getReplacePaymentmethods;

				saveShoppingCartId(shoppingCartId);
				initializeFundingSources({
					loadProductLineItemFunds,
					fundingSources, // might be empty
					restrictFunding,
					refetchFundingSources: async () => (await getReplacePaymentMethods()).data.OrderRoute.getReplacePaymentmethods,
				});
			}
			catch (errorReport) {
				// we're not redirecting anywhere. Prepare the form for the next submit.
				noticeError(null, levels.info, errorReport, `Get Payment Methods Failed`);
				formHelper.validationErrorHandler(errorReport);
				return;
			}
			finally {
				setSubmitting(false);
			}

			addProduct(
				new WSMediaProduct({
					...charlieCard,
					mediaRestrictions: charlieCard.mediaRestrictions?.map(restriction => new WSMediaRestriction(restriction)),
					display: charlieCard.display?.map(group => new WSProductDisplay(group)),
				 }),
				1,
			);

			setStep(PURCHASE_REPLACEMENT);
			return;
		}

		if (actionType === CARD_ACTIONS.blockCard) {
			let validated;
			try {
				validated = await formHelper.startValidation(true);
			}
			catch (errorReport) {
				setSubmitting(false);
				// yup validation failed, but those errors are already in state
				formHelper.validationErrorHandler(errorReport);
				return;
			}

			const primaryToken = findPrimaryToken(transitAccountQdata?.transitAccountQ.tokens);

			GoogleAnalytics.logEvent("User is attempting to block/unblock a Charlie Card");

			const variables = {
				tokenId: primaryToken.tokenId,
				tokenAction: TOKEN.ACTION.block,
				subsystemAccountReference,
				reasonCode: reasonType.reasonCodeId,
				notes: validated[ `${reasonType.reasonCodeId}_message` ],
			};

			const { subscriptions } = transitAccountQdata?.transitAccountQ;
			if (subscriptions?.length > 0) {
				const wsAutoloadSummaries = [ ...subscriptions ];
				do {
					// Consume wsAutoloadSummary at index 0
					const wsAutoloadSummary = wsAutoloadSummaries.shift();

					let response;
					try {

						response = await cancelAutoloadMutation(
							apolloClient,
							wsAutoloadSummary.subscriptionId,
							subsystemAccountReference,
						);
					}
					catch (errorReport) {
						noticeError(null, levels.info, errorReport, `Cancel reload subscription failed`);
						setValidationState({
							[ FormHelper.getStateErrorField('cancel-subscription') ]: (
								<CmsContentRenderer.Span
									contentKey={cms.blockAutoloadError}
									fallbackValue={'An error occurred while trying to cancel reload on this card. Try cancelling reload first.'}
								/>
							),
						});
						setSubmitting(false);

						// Error is consumed. Lets terminate the loop.
						return;
					}

					if (get(response, "data.OrderRoute.CancelSubscription.success", false)) {
						if (!wsAutoloadSummaries.length) {
							// We have no more wsAutoloadSummary to handle. End transaction.
							break;
						}

					}
					else {
						// Something broke. Terminate loop.
						break;
					}
				}
				while (wsAutoloadSummaries.length);
			}

			try {
				await graphqlErrorMiddleware(
					apolloClient.mutate({
						mutation: TOKEN_ACTION,
						variables,
						refetchQueries: [
							...getTransitAccountRefetchQueries(subsystemAccountReference),
						],
					}));
			}
			catch (errorReport) {
				// we're not redirecting anywhere. Prepare the form for the next submit.
				noticeError(null, levels.info, errorReport, `Block Card Submit Failed`);
				formHelper.validationErrorHandler(errorReport);
				setSubmitting(false);
				return;
			}
			setSubmitting(false);

			onBlockSuccess();
		}
	};

	const didMount = () => {
		// We are unblocking, or replacing the card and need to bypass the first step, but still submit the form
		if (stepOffSet === BYPASS_TO_REPLACEMENT &&
			(Object.values(BLOCKED_REASON_MAPPING).find((reasonObj => reasonObj.reasonCode === seachParamReasonType)) ||
			seachParamActionType === CARD_ACTIONS.replaceCard)) {
			kickoffSubmit();
		};
	};

	if (redirect) {
		return redirect;
	};

	return (
		<CmsContentList list={Object.values(cms)}>{() => (
			<FormHelperProvider {...{ formHelperContext }}>
				<form
					data-qa="BlockOrReplaceTakeoverForm"
					ref={formRef}
					onSubmit={PreventDefault(kickoffSubmit)}
				>
					<div className={takeover.mainContent}>
						<div className={takeover.mainColumn}>
							<CardActionSelector
								canOrderCard={!!charlieCard}
								canBlockOnly={canBlockOnly}
							/>

							<div className={forms.actions}>
								<Lifecycles {...{ didMount }}>
									<Button
										isPrimary
										additionalClassNames={forms.action}
										type="submit"
										submitting={submitting}
										disabled={isInvalidMediaProduct}
									>
										<CmsContentRenderer.Span
											contentKey={cms.continueButton}
											fallbackValue="Continue"
										/>
									</Button>
								</Lifecycles>
							</div>
						</div>
					</div>
				</form>
			</FormHelperProvider>
		)}</CmsContentList>
	);
};

export default BlockOrReplaceSelect;
